/**
 * Browser Notification Utilities
 * Handles browser push notifications and permission management
 */

export type NotificationPermission = 'granted' | 'denied' | 'default';

/**
 * Check if browser supports notifications
 */
export const isNotificationSupported = (): boolean => {
  return 'Notification' in window;
};

/**
 * Get current notification permission status
 */
export const getNotificationPermission = (): NotificationPermission => {
  if (!isNotificationSupported()) {
    return 'denied';
  }
  return Notification.permission;
};

/**
 * Request notification permission from user
 */
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!isNotificationSupported()) {
    console.warn('Browser does not support notifications');
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    return 'denied';
  }

  try {
    const permission = await Notification.requestPermission();
    return permission;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return 'denied';
  }
};

export interface BrowserNotificationOptions {
  body?: string;
  icon?: string;
  badge?: string;
  tag?: string;
  requireInteraction?: boolean;
  silent?: boolean;
  data?: any;
  onClick?: () => void;
  onClose?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Show browser notification
 */
export const showBrowserNotification = (
  title: string,
  options: BrowserNotificationOptions = {}
): Notification | null => {
  if (!isNotificationSupported()) {
    console.warn('Browser does not support notifications');
    return null;
  }

  if (Notification.permission !== 'granted') {
    console.warn('Notification permission not granted');
    return null;
  }

  try {
    const notification = new Notification(title, {
      body: options.body,
      icon: options.icon || '/panther-logo.png',
      badge: options.badge || '/panther-logo.png',
      tag: options.tag,
      requireInteraction: options.requireInteraction || false,
      silent: options.silent || false,
      data: options.data,
    });

    if (options.onClick) {
      notification.onclick = () => {
        options.onClick?.();
        notification.close();
      };
    }

    if (options.onClose) {
      notification.onclose = () => {
        options.onClose?.();
      };
    }

    if (options.onError) {
      notification.onerror = (event) => {
        options.onError?.(new Error('Notification error'));
      };
    }

    return notification;
  } catch (error) {
    console.error('Error showing browser notification:', error);
    return null;
  }
};

/**
 * Close all notifications with specific tag
 */
export const closeNotificationsByTag = async (tag: string): Promise<void> => {
  // Note: This requires service worker registration
  // For now, we'll just log a warning
  console.warn('Closing notifications by tag requires service worker setup');
};

/**
 * Play notification sound
 */
let audioContext: AudioContext | null = null;
let notificationSoundBuffer: AudioBuffer | null = null;

export const playNotificationSound = async (): Promise<void> => {
  try {
    // Initialize AudioContext if needed
    if (!audioContext) {
      audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }

    // Load sound if not already loaded
    if (!notificationSoundBuffer) {
      const response = await fetch('/sounds/notification.mp3');
      const arrayBuffer = await response.arrayBuffer();
      notificationSoundBuffer = await audioContext.decodeAudioData(arrayBuffer);
    }

    // Play sound
    const source = audioContext.createBufferSource();
    source.buffer = notificationSoundBuffer;
    source.connect(audioContext.destination);
    source.start(0);
  } catch (error) {
    console.error('Error playing notification sound:', error);
    // Fallback to HTML5 Audio if Web Audio API fails
    try {
      const audio = new Audio('/sounds/notification.mp3');
      audio.volume = 0.5;
      await audio.play();
    } catch (fallbackError) {
      console.error('Error playing notification sound (fallback):', fallbackError);
    }
  }
};

/**
 * Check if notification should be shown based on document visibility
 * Browser notifications typically only show when page is not focused
 */
export const shouldShowBrowserNotification = (): boolean => {
  return document.hidden || !document.hasFocus();
};
